var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Chip, Skeleton, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageContainer, PageContent } from "@ui/components/wraper";
import PageHeader from "@ui/components/header/PageHeader";
import { PrimaryGrid, GridFilters, GridCellStack } from "@ui/components/grid";
import { StatusIndicator } from "@ui/components/status";
import { SignalMeter } from "@ui/components/icons/SignalMeter";
import { SButton } from "@ui/components/buttons/SButton";
import ConfirmDialog from "@ui/components/dialog/Confirmation";
import { colCenter } from "@ui/components/style/style";
import { toast } from "@ui/utils/toaster";
import { usePermissions } from "@ui/hooks/usePermissions";
import { Permission } from "@utility/permission-checker";
import { getLatestApActivityIso } from "@utility/access-point-monitoring";
import { formatUtcToLocalDate, formatUtcToLocalTime } from "@application/common/helpers/date-util";
import { accessPointsApi } from "@api/enpoints/access-points-api";
import { apiErrorMessage, clientsApi } from "@api/enpoints/clients-api";
const toRow = (ap) => {
    var _a;
    return ({
        id: ap.id || ap.accessPointUID,
        name: ap.accessPointName || ap.accessPointUID,
        deviceId: ap.accessPointUID,
        status: ap.isActive ? "active" : "inactive",
        location: ap.clientLocationName || "—",
        lastUpdate: getLatestApActivityIso(ap),
        nodeCount: (_a = ap.nodeCount) !== null && _a !== void 0 ? _a : 0,
    });
};
/** A client's counts and its access points (the AP list filtered by client, server-side). */
const ClientDetailPage = () => {
    var _a;
    const { t } = useTranslation(["common"]);
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const { clientId = "" } = useParams();
    const [client, setClient] = useState(null);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState({ pageNumber: 1, pageSize: 25 });
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [toRemove, setToRemove] = useState(null);
    const [removing, setRemoving] = useState(false);
    const requestId = useRef(0);
    const loadClient = useCallback(() => {
        clientsApi
            .getById(clientId)
            .then((response) => { var _a; return setClient((_a = response.data.data) !== null && _a !== void 0 ? _a : null); })
            .catch((error) => {
            toast.error(apiErrorMessage(error, t("common:catalog.clients.loadOneFailed", "Could not load the client.")));
            navigate("/clients");
        });
    }, [clientId, navigate, t]);
    useEffect(() => {
        loadClient();
    }, [loadClient]);
    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(search.trim()), 500);
        return () => clearTimeout(handle);
    }, [search]);
    useEffect(() => setPage((p) => (Object.assign(Object.assign({}, p), { pageNumber: 1 }))), [debouncedSearch]);
    const loadAps = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        const id = ++requestId.current;
        setLoading(true);
        try {
            const response = yield accessPointsApi.getAllAccessPoints({
                pageNumber: page.pageNumber,
                pageSize: page.pageSize,
                sortColumn: "accessPointName",
                sortDirection: "asc",
                filters: { clientIds: [clientId], globalSearch: debouncedSearch || undefined },
            });
            if (id !== requestId.current)
                return;
            setRows(((_b = (_a = response.data.data) === null || _a === void 0 ? void 0 : _a.items) !== null && _b !== void 0 ? _b : []).map(toRow));
            setTotal((_d = (_c = response.data.data) === null || _c === void 0 ? void 0 : _c.totalCount) !== null && _d !== void 0 ? _d : 0);
        }
        catch (error) {
            if (id === requestId.current)
                toast.error(apiErrorMessage(error, t("common:catalog.clients.apsLoadFailed", "Could not load the access points.")));
        }
        finally {
            if (id === requestId.current)
                setLoading(false);
        }
    }), [clientId, page, debouncedSearch, t]);
    useEffect(() => {
        loadAps();
    }, [loadAps]);
    const removeFromClient = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!toRemove)
            return;
        setRemoving(true);
        try {
            yield accessPointsApi.updateAccessPointGeneral(toRemove.deviceId, {
                accessPointUID: toRemove.deviceId,
                client: { clientId: null, clientLocationId: null },
            });
            toast.success(t("common:catalog.clients.apRemoved", "Access point removed from the client."));
            loadAps();
            loadClient();
        }
        catch (error) {
            toast.error(apiErrorMessage(error, t("common:catalog.clients.apRemoveFailed", "Could not remove the access point.")));
        }
        finally {
            setRemoving(false);
            setToRemove(null);
        }
    });
    const canEditAps = hasPermission(Permission.ACCESSPOINTS_EDIT);
    const columns = useMemo(() => [
        {
            field: "ap",
            headerName: t("common:grid.ap", "AP"),
            minWidth: 80,
            filterable: false,
            sortable: false,
            renderCell: (params) => (React.createElement(Box, { sx: { display: "flex", justifyContent: "center" } },
                React.createElement(SignalMeter, { width: 66, height: 57, variant: params.row.status === "active" ? "success" : "warning" }))),
        },
        {
            field: "nameId",
            headerName: t("common:grid.nameId", "Name & ID"),
            flex: 3,
            minWidth: 200,
            sortable: false,
            renderCell: (params) => (React.createElement(GridCellStack, { topText: params.row.name, bottomText: params.row.deviceId, topStyle: { fontWeight: 500 }, bottomVariant: "caption", bottomColor: "text.secondary" })),
        },
        {
            field: "nodeCount",
            headerName: t("common:grid.nodeCount", "Nodes"),
            flex: 1.5,
            minWidth: 110,
            sortable: false,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => React.createElement(Chip, { label: `${params.row.nodeCount} ${t("common:grid.devices", "device")}`, size: "small", variant: "soft", color: "default" }),
        },
        {
            field: "status",
            headerName: t("common:grid.status", "Status"),
            flex: 1.5,
            minWidth: 110,
            sortable: false,
            renderCell: (params) => React.createElement(StatusIndicator, { variant: params.row.status }),
        },
        {
            field: "location",
            headerName: t("common:catalog.clients.location", "Location"),
            flex: 2,
            minWidth: 140,
            sortable: false,
        },
        {
            field: "lastUpdate",
            headerName: t("common:grid.lastUpdate", "Last Update"),
            flex: 1.5,
            minWidth: 140,
            sortable: false,
            renderCell: (params) => (React.createElement(GridCellStack, { topText: formatUtcToLocalDate(params.row.lastUpdate), bottomText: formatUtcToLocalTime(params.row.lastUpdate) })),
        },
        {
            field: "actions",
            headerName: t("common:grid.actions", "Actions"),
            minWidth: 260,
            sortable: false,
            headerAlign: "center",
            renderCell: (params) => (React.createElement(Box, { sx: Object.assign(Object.assign({}, colCenter), { gap: 1, justifyContent: "end" }) },
                canEditAps && (React.createElement(SButton, { variant: "soft", color: "error", size: "medium", onClick: (e) => {
                        e.stopPropagation();
                        setToRemove(params.row);
                    } }, t("common:actions.remove", "Remove"))),
                React.createElement(SButton, { variant: "soft", size: "medium", color: "primary", onClick: (e) => {
                        e.stopPropagation();
                        navigate(`/dashboard/${params.row.deviceId}`);
                    } }, t("common:actions.nodes", "Nodes")),
                canEditAps && (React.createElement(SButton, { variant: "outlined", size: "medium", onClick: (e) => {
                        e.stopPropagation();
                        navigate(`/dashboard/${params.row.deviceId}/edit`);
                    } }, t("common:actions.settings", "Settings"))))),
        },
    ], [t, canEditAps, navigate]);
    // Design: one pill-shaped light-blue bar; Client gets a wide column, the three counts equal ones, Edit on the right.
    const stat = (label, value) => (React.createElement(Box, { sx: { minWidth: 0 } },
        React.createElement(Typography, { noWrap: true, sx: { fontSize: 13, color: "#464F5C", lineHeight: 1.4 } }, label),
        React.createElement(Typography, { noWrap: true, sx: { fontSize: 14, color: "#171C24", lineHeight: 1.4, mt: 0.75 } }, value)));
    return (React.createElement(PageContainer, null,
        React.createElement(PageHeader, { title: t("common:catalog.clients.detail", "Client Detail"), showBackButton: true }),
        React.createElement(PageContent, null,
            React.createElement(Box, { sx: {
                    bgcolor: "#E4EFF7",
                    border: "1px solid #D2E6F5",
                    borderRadius: { xs: 4, md: "40px" },
                    px: 2,
                    py: 2,
                    mb: 3,
                    minHeight: 77,
                    display: "grid",
                    alignItems: "center",
                    columnGap: 2,
                    rowGap: 1.5,
                    gridTemplateColumns: {
                        xs: "repeat(3, minmax(0, 1fr))",
                        md: "minmax(0, 2fr) repeat(3, minmax(0, 1fr)) auto",
                    },
                } }, client ? (React.createElement(React.Fragment, null,
                React.createElement(Box, { sx: { gridColumn: { xs: "1 / -1", md: "auto" } } }, stat(t("common:catalog.clients.client", "Client"), client.name)),
                stat(t("common:catalog.clients.locations", "Locations"), client.locations.length),
                stat(t("common:catalog.clients.linkedAps", "Linked APs"), client.linkedApCount),
                stat(t("common:catalog.clients.linkedNodes", "Linked Nodes"), client.linkedNodeCount),
                React.createElement(Box, { sx: { gridColumn: { xs: "1 / -1", md: "auto" }, display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" } } }, hasPermission(Permission.CLIENTS_EDIT) && (React.createElement(SButton, { variant: "contained", size: "medium", onClick: () => navigate(`/clients/${clientId}`) }, t("common:actions.edit", "Edit")))))) : (React.createElement(Skeleton, { variant: "rounded", height: 44, sx: { gridColumn: "1 / -1", bgcolor: "rgba(255,255,255,0.5)" } }))),
            React.createElement(GridFilters, { title: t("common:catalog.clients.apCount", { count: total, defaultValue: "{{count}} Access Points" }), loading: loading, columns: [], filters: {}, onChange: () => undefined, searchProp: search, handleSearchChange: (e) => setSearch(e.target.value), hideFilter: true }),
            React.createElement(PrimaryGrid, { rows: rows, columns: columns, loading: loading, heightVariant: "dashboard", checkboxSelection: false, paginationMode: "server", rowCount: total, paginationModel: { page: page.pageNumber - 1, pageSize: page.pageSize }, onPaginationModelChange: (model) => setPage({ pageNumber: model.page + 1, pageSize: model.pageSize }), pageSizeOptions: [5, 25, 50] })),
        React.createElement(ConfirmDialog, { isOpen: !!toRemove, onClose: () => setToRemove(null), onConfirm: removeFromClient, loading: removing, actionText: t("common:actions.remove", "Remove"), customMessage: t("common:catalog.clients.confirmRemoveAp", {
                name: (_a = toRemove === null || toRemove === void 0 ? void 0 : toRemove.name) !== null && _a !== void 0 ? _a : "",
                defaultValue: "Remove {{name}} from this client? Its client and location are cleared.",
            }), variant: "warning" })));
};
export default ClientDetailPage;
